import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/providers/Permissions.provider';
import { useClientData } from '@/providers/ClientData.provider';
import {} from '@/components/Form/types';
import { Button } from '@/components/UI';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import useContacts from '@/hooks/useApi/useContacts';
import { Box, Icon, Select, FieldData, HighlightedText } from 'vopay';

import { PaymentFieldDefault, Contact } from './types';

export const useContactFields = (
  contacts: Contact[],

  onContactSearch: (val: string) => void,
  fetchingContacts: boolean,
): FieldData<PaymentFieldDefault>[] => {
  const {
    response: { clientAccountsEnabled, Country },
  } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  const [isNewContact, setIsNewContact] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { permissions } = usePermissions();

  const { fetchClientAccount } = useClientAccounts();
  const { fetchContact } = useContacts();

  useEffect(() => {
    if (!fetchingContacts) {
      setIsLoading(fetchingContacts);
    }
  }, [fetchingContacts]);

  return [
    {
      label: () => t('Fields.clientAccount.label'),
      fieldName: 'Contact',
      getInputState: () => 'required',
      showField: () => clientAccountsEnabled,
      serializeField: (values) => (values.Contact === 'new' ? undefined : values.Contact),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            filterable
            loading={isLoading}
            onFilter={(e) => {
              e.preventDefault();
              setIsLoading(true);

              e.debounce(() => {
                onContactSearch(e.filter);
              }, 1000);
            }}
            options={contacts}
            optionValue={(o) => o.id}
            renderOption={(o, { Option, SelectedIcon, selected, preventDefault, disabled, filter }) => (
              <Option
                onClick={() => {
                  if (disabled) {
                    preventDefault();
                  } else {
                    setIsNewContact(false);
                    ctx.setFieldValue('OverrideEmailAddress', o.email);
                  }
                }}
              >
                <div>
                  <HighlightedText text={o.name} searchTerm={filter} />
                </div>
                {selected && <SelectedIcon />}
              </Option>
            )}
            renderSelectedValue={(data, { Placeholder }) => {
              if (!data) return <Placeholder />;

              return data.name;
            }}
            placeholder={t('Fields.clientAccount.placeholder')}
            {...fieldData}
            onChange={async (e, obj) => {
              fieldData.onChange(e);
              ctx.setFieldTouched('PaymentMethod', false);
              ctx.setFieldValue('PaymentMethod', '');
              ctx.setFieldTouched('PaymentSelectionMethod', false);
              ctx.setFieldValue('PaymentSelectionMethod', '');
              ctx.setFieldTouched('Amount', false);
              ctx.setFieldValue('Amount', '0.00');
              if (obj.value === 'new') return;

              if (!clientAccountsEnabled) return;
              const account = await fetchClientAccount(obj.value);

              if (!account) return;

              ctx.setFieldTouched('IdentificationNumber', false);
              ctx.setFieldValue('IdentificationNumber', account.IdentificationNumber);
              ctx.setFieldValue('RecipientPhoneNumber', account.PhoneNumber || '');
              ctx.setFieldValue('ClientAccountPhoneNumber', account.PhoneNumber || '');
              ctx.setFieldValue('Address1', account.Address1 || '');
              ctx.setFieldValue('Address2', account.Address2 || '');
              ctx.setFieldValue('City', account.City || '');
              ctx.setFieldValue('Province', account.Province || '');
              ctx.setFieldValue('PostalCode', account.PostalCode || '');
              ctx.setFieldValue('Country', account.Country || Country || '');
            }}
          />
        );
      },

      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('Fields.contact.label'),
      fieldName: 'Contact',
      getInputState: () => 'required',
      showField: () => !clientAccountsEnabled,
      serializeField: (values) => (values.Contact === 'new' ? undefined : values.Contact),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            filterable
            loading={isLoading}
            onFilter={(e) => {
              e.preventDefault();
              setIsLoading(true);

              e.debounce(() => {
                onContactSearch(e.filter);
              }, 1000);
            }}
            options={contacts}
            optionValue={(o) => o.id}
            renderOption={(o, { Option, SelectedIcon, selected, preventDefault, disabled, filter }) => (
              <Option
                onClick={() => {
                  if (disabled) {
                    preventDefault();
                  } else {
                    setIsNewContact(false);
                    ctx.setFieldValue('OverrideEmailAddress', o.email);
                  }
                }}
              >
                <div>
                  <HighlightedText text={o.name} searchTerm={filter} />
                </div>
                {selected && <SelectedIcon />}
              </Option>
            )}
            renderSelectedValue={(data, { Placeholder }) => {
              if (isNewContact) return t('Fields.contact.addNewContactLabel');

              if (!data) return <Placeholder />;

              return data.name;
            }}
            renderDropdownFooter={({ close }) => (
              <Box flx="col">
                {permissions.AddBeneficiaries && (
                  <Button
                    colorScheme="secondary"
                    onClick={() => {
                      setIsNewContact(true);
                      ctx.setFieldValue('Contact', 'new');
                      close();
                    }}
                  >
                    <Box flx="row" gapSize="8px" jc="center" ai="center">
                      <Icon height="20px" width="20px" icon="add" color="medium-dark" />
                      <span>{t('Fields.contact.addNewContactButton')}</span>
                    </Box>
                  </Button>
                )}
              </Box>
            )}
            placeholder={t('Fields.contact.placeholder')}
            {...fieldData}
            onChange={async (e, obj) => {
              fieldData.onChange(e);
              if (obj.value === 'new') {
                ctx.setValues({
                  ...ctx.values,
                  Contact: obj.value,
                  PaymentMethod: 'new',
                  PaymentSelectionMethod: '',
                  EtransferConnectionType: 'email',
                  Country: Country || '',
                  Answer: '',
                  ClientReferenceNumber: '',
                  Question: '',
                  RecipientEmailAddress: '',
                  RecipientName: '',
                  RecipientPhoneNumber: '',
                  FinancialInstitutionNumber: '',
                  BranchTransitNumber: '',
                  ABARoutingNumber: '',
                  AccountNumber: '',
                  Address1: '',
                  City: '',
                  Province: '',
                  PostalCode: '',
                  FirstName: '',
                  LastName: '',
                  CompanyName: '',
                });

                ctx.setTouched({
                  ...ctx.touched,
                  Answer: false,
                  ClientReferenceNumber: false,
                  Question: false,
                  RecipientEmailAddress: false,
                  RecipientName: false,
                  RecipientPhoneNumber: false,
                  FinancialInstitutionNumber: false,
                  BranchTransitNumber: false,
                  ABARoutingNumber: false,
                  AccountNumber: false,
                  Address1: false,
                  City: false,
                  Province: false,
                  Country: false,
                  PostalCode: false,
                  FirstName: false,
                  LastName: false,
                  CompanyName: false,
                });
              } else {
                // Fetch the full contact details to get address
                let contactAddress1 = '';
                let contactAddress2 = '';
                let contactCity = '';
                let contactProvince = '';
                let contactPostalCode = '';
                let contactCountry = '';

                try {
                  const contact = await fetchContact(obj.value);
                  if (contact) {
                    contactAddress1 = contact.Address1 || '';
                    contactAddress2 = contact.Address2 || '';
                    contactCity = contact.City || '';
                    contactProvince = contact.Province || '';
                    contactPostalCode = contact.PostalCode || '';
                    contactCountry = contact.Country || Country || '';
                  }
                } catch (err) {
                  console.error('Error fetching contact details:', err);
                }

                ctx.setValues({
                  ...ctx.values,
                  Contact: obj.value,
                  PaymentMethod: '',
                  PaymentSelectionMethod: '',
                  EtransferConnectionType: 'email',
                  ProvideOrRequestPaymentInformation: 'provide',
                  Country: contactCountry || Country || '',
                  Amount: '',
                  Answer: '',
                  ClientReferenceNumber: '',
                  Question: '',
                  RecipientEmailAddress: '',
                  RecipientName: obj.data?.name || '',
                  RecipientPhoneNumber: '',
                  FinancialInstitutionNumber: '',
                  BranchTransitNumber: '',
                  ABARoutingNumber: '',
                  AccountNumber: '',
                  Address1: contactAddress1,
                  Address2: contactAddress2,
                  City: contactCity,
                  Province: contactProvince,
                  PostalCode: contactPostalCode,
                  FirstName: '',
                  LastName: '',
                  CompanyName: '',
                });

                ctx.setTouched({
                  ...ctx.touched,
                  Amount: false,
                  Answer: false,
                  ClientReferenceNumber: false,
                  Question: false,
                  RecipientEmailAddress: false,
                  RecipientName: false,
                  RecipientPhoneNumber: false,
                  FinancialInstitutionNumber: false,
                  BranchTransitNumber: false,
                  ABARoutingNumber: false,
                  AccountNumber: false,
                  Address1: false,
                  City: false,
                  Province: false,
                  Country: false,
                  PostalCode: false,
                  FirstName: false,
                  LastName: false,
                  CompanyName: false,
                  ProvideOrRequestPaymentInformation: false,
                  PaymentMethod: false,
                  PaymentSelectionMethod: false,
                });

                const nameParts = obj.data?.name?.trim().split(' ') || [];

                const isCompanyName = nameParts.length > 2 || nameParts.length < 2;
                if (isCompanyName) {
                  ctx.setFieldValue('CompanyName', nameParts.join(' ') || '');
                } else {
                  ctx.setFieldValue('FirstName', nameParts[0] || '');
                  ctx.setFieldValue('LastName', nameParts.slice(1).join(' ') || '');
                }
              }
            }}
          />
        );
      },

      validate: () => Yup.mixed().required(),
    },
  ];
};
